import React, { useEffect, useState } from "react";
import client from "@/lib/api";
import { toast } from "sonner";
import { FileText, ImageOff, Loader2, ExternalLink, Paperclip, Trash2 } from "lucide-react";

function AuthThumb({ path, contentType, filename, onDelete }) {
  const [src, setSrc] = useState(null);
  const [failed, setFailed] = useState(false);
  const isPdf = contentType === "application/pdf" || (filename || "").toLowerCase().endsWith(".pdf");
  useEffect(() => {
    if (isPdf) return;
    let url;
    client.get(`/forms/attachment/${path}`, { responseType: "blob" })
      .then((r) => { url = URL.createObjectURL(r.data); setSrc(url); })
      .catch(() => setFailed(true));
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [path, isPdf]);

  const open = async () => {
    try {
      const r = await client.get(`/forms/attachment/${path}`, { responseType: "blob" });
      const u = URL.createObjectURL(r.data);
      window.open(u, "_blank");
      setTimeout(() => URL.revokeObjectURL(u), 60000);
    } catch { /* noop */ }
  };

  return (
    <div className="relative group">
      {isPdf ? (
        <button data-testid="attachment-pdf" onClick={open}
          className="flex items-center gap-1.5 px-2 py-2 h-16 border border-[#F59E0B]/50 bg-[#141518] text-[#F59E0B] text-[10px] font-mono hover:bg-[#F59E0B]/10 transition-colors">
          <FileText size={14} /> <span className="max-w-[110px] truncate">{filename || "PDF"}</span> <ExternalLink size={10} />
        </button>
      ) : (
        <button data-testid="attachment-image" onClick={open} title={filename}
          className="w-16 h-16 border border-[#2C2D35] bg-[#0d0e11] overflow-hidden flex items-center justify-center hover:border-[#3B82F6] transition-colors">
          {failed ? <ImageOff size={16} className="text-[#4B5563]" />
            : src ? <img src={src} alt={filename} className="w-full h-full object-cover" />
            : <Loader2 size={14} className="animate-spin text-[#9CA3AF]" />}
        </button>
      )}
      {onDelete && (
        <button data-testid={`attachment-delete-${filename}`} title="Remove attachment"
          onClick={() => onDelete(path, filename)}
          className="absolute -top-1.5 -right-1.5 bg-[#DC2626] text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
          <Trash2 size={12} />
        </button>
      )}
    </div>
  );
}

export default function AttachmentStrip({ attachments, label = "Attachments", formId, canDelete, onChanged }) {
  const [busy, setBusy] = useState(false);
  if (!attachments || attachments.length === 0) return null;
  const deletable = Boolean(formId && canDelete);

  const remove = async (path, filename) => {
    if (!formId || busy) return;
    if (!window.confirm(`Remove "${filename}" from this record? This cannot be undone.`)) return;
    setBusy(true);
    try {
      await client.delete(`/forms/${formId}/attachments`, { params: { path } });
      toast.success("Attachment removed");
      onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not remove attachment"); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="attachment-strip" className="mt-1.5">
      <div className="font-mono text-[10px] text-[#9CA3AF] uppercase mb-1.5 flex items-center gap-1">
        <Paperclip size={11} /> {label} ({attachments.length})
      </div>
      <div className="flex flex-wrap gap-2">
        {attachments.map((a, i) => (
          <AuthThumb key={i} path={a.path} contentType={a.content_type} filename={a.filename}
            onDelete={deletable ? remove : undefined} />
        ))}
      </div>
    </div>
  );
}
